/**
 * Spectrum-hosted Twilio webchat (live-webchat iframe) in guest mode (CR-414).
 *
 * Laya has no login, so there is no token to send and no logout to end a session with.
 * The guest session id does that job instead: the webchat keeps a conversation for as
 * long as it keeps receiving the same id, and discards it before booting for a new one.
 *
 * Message shapes mirror live-webchat/src/contracts/embed.ts (WebchatGuestInit).
 */

// Keep in step with WEBCHAT_SCHEMA_VERSION in live-webchat/src/contracts/embed.ts.
export const WEBCHAT_SCHEMA_VERSION = 1;

export const WEBCHAT_IFRAME_ID = "twilio-webchat-iframe";

// Laya EAP is Ireland-only, so it always uses the EU Flex account. Must be one of
// live-webchat's WEBCHAT_REGIONS.
export const WEBCHAT_REGION = "EU";

// Must be one of live-webchat's GUEST_CLIENTS — it picks Laya's colours there, and Flex
// routes and reports Laya chats on it.
export const WEBCHAT_GUEST_CLIENT = "laya_eap";

const GUEST_SESSION_STORAGE_KEY = "sl-webchat-guest-session-id";

const newGuestSessionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
};

/**
 * One id per browser tab: sessionStorage survives a reload (the chat resumes) but not a
 * new tab or browser (the next person gets a fresh chat). Never localStorage — that would
 * hand every visitor on a shared machine the same conversation.
 *
 * If storage is blocked the id lives only as long as this page load: no resume, but
 * never someone else's conversation.
 */
let fallbackGuestSessionId = null;

export const getGuestSessionId = () => {
  try {
    const existing = window.sessionStorage.getItem(GUEST_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const created = newGuestSessionId();
    window.sessionStorage.setItem(GUEST_SESSION_STORAGE_KEY, created);
    return created;
  } catch (error) {
    if (!fallbackGuestSessionId) fallbackGuestSessionId = newGuestSessionId();
    return fallbackGuestSessionId;
  }
};
